import clsx from 'clsx';
import tiktokIcon from '@client-app/assets/integration-icons/admin/tiktok-com.png';
import xIcon from '@client-app/assets/integration-icons/admin/x-com.png';
import facebookIcon from '@client-app/assets/integration-icons/admin/facebook-com.png';
import instagramIcon from '@client-app/assets/integration-icons/admin/instagram-com.png';
import youtubeIcon from '@client-app/assets/integration-icons/admin/youtube-com.png';
import amazonIcon from '@/assets/icons/amazon.svg';
import metaAdsIcon from '@common/assets/icons/meta-ads.svg';
import googleIcon from '@client-app/assets/integration-icons/google.svg';

import type { MarketPlatform } from './model';
import redditIcon from '@client-app/assets/integration-icons/admin/reddit-com.png';
import { getBrandIconFrameClassName } from '@/components/Settings/components/integrations/IntegrationCard';

const PLATFORM_LABELS: Record<MarketPlatform, string> = {
    google_search: 'Google Search',
    google_trends: 'Google Trends',
    meta: 'Meta Ads',
    tiktok: 'TikTok Ads',
    reddit: 'Reddit',
    x: 'X',
    youtube: 'YouTube',
    facebook: 'Facebook',
    instagram: 'Instagram',
    amazon: 'Amazon',
};

// Reuse the bundled Admin brand assets; static logos must not depend on CDN availability.
const PLATFORM_ICONS: Record<MarketPlatform, string> = {
    google_search: googleIcon,
    google_trends: googleIcon,
    meta: metaAdsIcon,
    tiktok: tiktokIcon,
    reddit: redditIcon,
    x: xIcon,
    youtube: youtubeIcon,
    facebook: facebookIcon,
    instagram: instagramIcon,
    amazon: amazonIcon,
};

// Match perceived weight, not just the source canvas: horizontal marks need
// more width while solid black square marks need slightly less area.
const PLATFORM_OPTICAL_SCALE: Record<MarketPlatform, number> = {
    meta: 1.15, tiktok: 0.95, reddit: 0.95, x: 0.88,
    youtube: 1.2, facebook: 1, instagram: 1, amazon: 1.05,
    google_search: 1, google_trends: 1,
};

interface PlatformIconProps {
    platform: MarketPlatform;
    size?: 'small' | 'medium' | 'integration' | 'display';
    muted?: boolean;
}

const PlatformIcon = ({
    platform,
    size = 'medium',
    muted = false,
}: PlatformIconProps) => {
    const label = PLATFORM_LABELS[platform];
    const baseSize = size === 'small' ? 17 : size === 'display' ? 27 : size === 'integration' ? 23 : 21;
    const opticalSize = baseSize * PLATFORM_OPTICAL_SCALE[platform];
    const brand = platform === 'facebook' ? 'Facebook Page' : platform === 'x' ? 'X Ads' : label;

    return (
        <span
            aria-label={label}
            className={clsx(
                'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-[7px]',
                size === 'small'
                    ? 'h-6 w-6'
                    : size === 'display'
                      ? 'h-10 w-10 !rounded-[9px]'
                      : size === 'integration'
                        ? 'h-9 w-9 !rounded-lg'
                        : 'h-8 w-8',
                getBrandIconFrameClassName(brand),
                muted && 'opacity-35 grayscale',
            )}
            role="img"
            title={label}
        >
            <img
                alt=""
                className="origin-center object-contain"
                style={{ width: opticalSize, height: opticalSize }}
                src={PLATFORM_ICONS[platform]}
            />
        </span>
    );
};

export const platformLabel = (platform: MarketPlatform): string =>
    PLATFORM_LABELS[platform];

export default PlatformIcon;
